import { useMutation } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import { ReportApis } from 'apis/ReportApis';
import ButtonExport from 'components/Button/ButtonExport';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import dayjs from 'dayjs';
import usePagination from 'hooks/usePagination';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import { generateUUID } from 'utils/constants/functions';
import FilterCustomerRevenue from './FilterMarketCoverage';
import TableCustomerRevenue from './TableMarketCoverage';

const MarketCoveragePage = () => {
  const { user } = useUser();
  const { setTitle, setBreadcrumb } = useTitle();
  const [data, setData] = useState<any>({});
  const [isShow, setIsShow] = useState(false);
  const [filters, setFilters] = useState<any>({});
  const { page, page_size } = usePagination();
  const navigate = useNavigate();
  const ref = useRef<any>(null);

  const defaultFilter = useMemo(() => {
    const userAny = user as any;

    if (user?.plantCode === '5000') return { year: dayjs() };

    if (user?.isSale) {
      const teamSale = userAny?.teamSales?.[0];

      return {
        year: dayjs(),
        plantId: userAny?.plantCode,
        teamSalesId: teamSale?.id || 'SALE_ID',
        teamSalesSubId: teamSale?.subId || 'SALE_SUB_ID',
        teamSalesName: teamSale?.name,
      };
    }

    return {
      year: dayjs(),
      plantId: userAny?.plantCode,
    };
  }, [user]);

  const { mutate: getCustomerRevenue, isPending: getCustomerRevenueLoading } =
    useMutation({
      mutationFn: ReportApis.getMarketCoverage,
      onSuccess: (data: any, variables: any) => {
        const result = data?.data?.getSalesProvinceReport || {};
        const items = (result.items || []).map((item: any) => ({
          ...item,
          id: generateUUID(),
        }));

        if (variables.type === 'export') {
          const items = result.items || [];

          return exportExcel_v2(
            variables.headerRow,
            items,
            'Báo cáo - Độ phủ thị trường',
          );
        }

        setData({
          ...result,
          items,
        });
      },
      onError: (error) => {
        console.log(error);
      },
    });

  const handleFilter = (values: any) => {
    setIsShow(true);
    setFilters(values);
    navigate(`?page=1&page_size=${page_size}`);
  };

  const handleCancel = () => {
    setIsShow(false);
    setFilters({ year: dayjs() });
  };

  const exportExcel = (columns: any) => {
    getCustomerRevenue({
      skip: 0,
      take: data.pageInfo?.totalItems || 1000,
      ...(filters.plantId && { plantId: filters.plantId }),
      ...(filters.year && { year: dayjs(filters.year).year() }),
      ...(filters.year && { month: dayjs(filters.year).month() + 1 }),
      type: 'export',
      headerRow: columns,
    });
  };

  const handleExportExcel = () => {
    if (!ref.current) return;

    ref.current.handleExportExcel();
  };

  useEffect(() => {
    setTitle('Độ phủ thị trường');
    setBreadcrumb([{ title: 'Báo cáo' }, { title: 'Độ phủ thị trường' }]);
  }, [setBreadcrumb, setTitle]);

  useEffect(() => {
    if (!isShow) return;

    getCustomerRevenue({
      skip: page_size * (page - 1),
      take: page_size,
      ...(filters.plantId && { plantId: filters.plantId }),
      ...(filters.year && { year: dayjs(filters.year).year() }),
      ...(filters.year && { month: dayjs(filters.year).month() + 1 }),
    });
  }, [
    filters.customerId,
    filters.plantId,
    filters.year,
    getCustomerRevenue,
    isShow,
    page,
    page_size,
  ]);

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'}>
        <Col>
          <ButtonExport disabled={!isShow} onClick={handleExportExcel} />
        </Col>
      </Row>
      <FilterCustomerRevenue
        onFilter={handleFilter}
        onCancel={handleCancel}
        defaultFilter={defaultFilter}
      />

      {isShow && (
        <TableCustomerRevenue
          ref={ref}
          exportExcel={exportExcel}
          loading={getCustomerRevenueLoading}
          data={data}
        />
      )}
    </>
  );
};

export default MarketCoveragePage;
